/**
 * VoiceRecordingModal — full-screen sheet on mobile, docked centered card on
 * desktop. Shown while Hey Andi is in any voice state (listening / transcribing
 * / thinking / speaking). Mimics the S.MPLE "Record Your Instructions" UX
 * with a live waveform and a pill action bar for stop/cancel/send.
 *
 * Props:
 *   open           : boolean
 *   onClose        : () => void   (parent should close on cancel and on idle)
 *   state          : VOICE_STATE  current state
 *   waveform       : number[]     normalised bars (0..1)
 *   continuousMode : boolean
 *   onToggleContinuous : () => void
 *   onCancel       : () => void   (trash bin)
 *   onStop         : () => void   (red square — finalise & transcribe)
 *   onStart        : () => void   (re-start when idle inside modal)
 */
import React from 'react';
import { Trash2, Square, Mic, Loader2, Activity, X } from 'lucide-react';
import { VOICE_STATE } from '../../hooks/useDerwentVoice';

const STATUS_TEXT = {
  [VOICE_STATE.IDLE]:         'TAP TO SPEAK',
  [VOICE_STATE.LISTENING]:    'LISTENING…',
  [VOICE_STATE.TRANSCRIBING]: 'TRANSCRIBING…',
  [VOICE_STATE.THINKING]:     'HEY DERWENT IS THINKING…',
  [VOICE_STATE.SPEAKING]:     'HEY DERWENT IS SPEAKING…',
};

const Waveform = ({ bars, active }) => (
  <div
    className="flex items-end justify-center gap-1.5 h-32 sm:h-40 my-auto select-none"
    aria-hidden="true"
    data-testid="voice-waveform"
  >
    {bars.map((v, i) => {
      // Base height even when idle so the bars are visible; amplitude
      // grows with volume. Min ~6%, max 100%.
      const h = active ? Math.max(0.08, Math.min(1, v)) : 0.08;
      return (
        <span
          key={i}
          className="block rounded-full bg-blue-600"
          style={{
            width: '5px',
            height: `${h * 100}%`,
            transition: 'height 90ms linear, opacity 200ms ease',
            opacity: active ? 0.85 + h * 0.15 : 0.35,
          }}
        />
      );
    })}
  </div>
);

const VoiceRecordingModal = ({
  open,
  onClose,
  state,
  waveform,
  continuousMode,
  onToggleContinuous,
  onCancel,
  onStop,
  onStart,
}) => {
  if (!open) return null;

  const isListening    = state === VOICE_STATE.LISTENING;
  const isTranscribing = state === VOICE_STATE.TRANSCRIBING;
  const isThinking     = state === VOICE_STATE.THINKING;
  const isSpeaking     = state === VOICE_STATE.SPEAKING;
  const showWaveform   = isListening || isSpeaking;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-modal-title"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-150"
      data-testid="voice-recording-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full sm:w-[420px] sm:max-w-[92vw] bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 sm:rounded-[32px] rounded-t-[32px] border-t sm:border border-slate-200 dark:border-slate-700 shadow-[0_-12px_60px_rgba(0,0,0,0.55)] sm:shadow-2xl flex flex-col items-center px-6 pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] animate-in slide-in-from-bottom duration-300"
      >
        {/* Drag handle (mobile sheet affordance) */}
        <div className="sm:hidden w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600 mb-3" />

        {/* Header row — close + continuous mode toggle */}
        <div className="w-full flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
            aria-label="Close voice panel"
            data-testid="voice-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={onToggleContinuous}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide border transition-colors ${
              continuousMode
                ? 'bg-blue-600 text-white border-transparent'
                : 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40'
            }`}
            aria-pressed={continuousMode}
            data-testid="continuous-mode-toggle"
            title={continuousMode ? 'Auto-listen between turns: ON' : 'Auto-listen between turns: OFF'}
          >
            <Activity className="w-3.5 h-3.5" />
            {continuousMode ? 'Hands-free ON' : 'Hands-free'}
          </button>
        </div>

        <h2
          id="voice-modal-title"
          className="text-xl sm:text-2xl font-semibold text-center tracking-tight text-slate-900 dark:text-slate-100"
        >
          {isThinking || isTranscribing ? 'Working on it…' : isSpeaking ? 'Hey Andi' : 'Talk to Hey Andi'}
        </h2>

        {/* Visualiser */}
        <div className="w-full flex flex-col items-center justify-center my-6 min-h-[8rem]">
          {(isTranscribing || isThinking) ? (
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          ) : (
            <Waveform bars={waveform} active={showWaveform} />
          )}
        </div>

        <div
          className={`text-[11px] uppercase tracking-[0.22em] font-semibold mb-6 ${
            isListening
              ? 'text-rose-500 animate-pulse'
              : isSpeaking
              ? 'text-blue-600 animate-pulse'
              : 'text-slate-500 dark:text-slate-400'
          }`}
          aria-live="polite"
          data-testid="voice-status-text"
        >
          {STATUS_TEXT[state] || ''}
        </div>

        {/* Action bar — trash | record/stop | accent */}
        <div
          className="w-full flex items-center justify-center gap-4"
          data-testid="voice-action-bar"
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={!isListening && !isTranscribing}
            className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-rose-100 dark:hover:bg-rose-950/30 hover:text-rose-600 dark:hover:text-rose-400 transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Cancel recording"
            data-testid="voice-cancel-btn"
          >
            <Trash2 className="w-5 h-5" />
          </button>

          {isListening ? (
            <button
              type="button"
              onClick={onStop}
              className="h-16 w-16 rounded-full bg-rose-500 text-white shadow-[0_8px_24px_rgba(244,63,94,0.45)] hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
              aria-label="Stop and send"
              data-testid="voice-stop-btn"
            >
              <Square className="w-6 h-6" fill="currentColor" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onStart}
              disabled={isTranscribing || isThinking}
              className="h-16 w-16 rounded-full bg-blue-600 text-white shadow-[0_8px_24px_rgba(37,99,235,0.45)] hover:scale-105 active:scale-95 transition-transform flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              aria-label="Start recording"
              data-testid="voice-start-btn"
            >
              <Mic className="w-6 h-6" />
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100 transition-colors flex items-center justify-center"
            aria-label="Done"
            data-testid="voice-done-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceRecordingModal;
